import { type CreateMaintenanceProjectRequest } from '@gb-ticket/shared-types';
import { Controller, useFormContext } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return (
    <p className="text-sm text-destructive" role="alert">
      {message}
    </p>
  );
}

export function MaintenanceProjectStammdatenFields() {
  const { register, control, formState } = useFormContext<CreateMaintenanceProjectRequest>();
  const errors = formState.errors;

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">Stammdaten</h2>
      <div className="space-y-2">
        <Label htmlFor="proj-name">Projektname</Label>
        <Input id="proj-name" className="min-h-11" {...register('name')} />
        <FieldError message={errors.name?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="proj-no">Projektnummer (optional)</Label>
        <Input id="proj-no" className="min-h-11" {...register('projectNumber')} />
        <FieldError message={errors.projectNumber?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="proj-desc">Beschreibung (optional)</Label>
        <Textarea id="proj-desc" rows={4} {...register('description')} />
        <FieldError message={errors.description?.message} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="proj-active">Aktiv</Label>
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Switch
              id="proj-active"
              checked={field.value}
              onCheckedChange={field.onChange}
              aria-label="Projekt ist aktiv"
            />
          )}
        />
      </div>
    </div>
  );
}

export function MaintenanceProjectSiteAddressFields() {
  const { register, formState } = useFormContext<CreateMaintenanceProjectRequest>();
  const errors = formState.errors;

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">Standortadresse</h2>
      <div className="space-y-2">
        <Label htmlFor="proj-line1">Straße und Hausnummer</Label>
        <Input id="proj-line1" className="min-h-11" {...register('siteAddress.line1')} />
        <FieldError message={errors.siteAddress?.line1?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="proj-line2">Adresszeile 2 (optional)</Label>
        <Input id="proj-line2" className="min-h-11" {...register('siteAddress.line2')} />
        <FieldError message={errors.siteAddress?.line2?.message} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="proj-plz">PLZ</Label>
          <Input id="proj-plz" className="min-h-11" {...register('siteAddress.postalCode')} />
          <FieldError message={errors.siteAddress?.postalCode?.message} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="proj-city">Ort</Label>
          <Input id="proj-city" className="min-h-11" {...register('siteAddress.city')} />
          <FieldError message={errors.siteAddress?.city?.message} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="proj-country">Land</Label>
        <Input
          id="proj-country"
          className="min-h-11 max-w-[6rem] uppercase"
          maxLength={2}
          {...register('siteAddress.country')}
        />
        <p className="text-xs text-muted-foreground">
          Zweistelliges Länderkürzel nach ISO 3166-1, z. B. DE.
        </p>
        <FieldError message={errors.siteAddress?.country?.message} />
      </div>
    </div>
  );
}
